import React, { useState, useEffect } from "react";
import { API } from "../../App";
import { toast } from "sonner";
import axios from "axios";
import {
  Briefcase, Plus, Trash2, ChevronRight, Clock, MapPin,
  Users, DollarSign, AlertCircle, CheckCircle, Play, Pause, X,
  Search, Filter, FileText, Wrench, AlertTriangle, ExternalLink
} from "lucide-react";

const STATUS_CONFIG = {
  scheduled: { label: "Scheduled", color: "bg-cyan-500/20 text-cyan-400" },
  assigned: { label: "Assigned", color: "bg-blue-500/20 text-blue-400" },
  accepted: { label: "Accepted", color: "bg-teal-500/20 text-teal-400" },
  needs_review: { label: "Needs Review", color: "bg-yellow-500/20 text-yellow-400" },
  approved: { label: "Approved", color: "bg-emerald-500/20 text-emerald-400" },
  in_progress: { label: "In Progress", color: "bg-orange-500/20 text-orange-400" },
  completed: { label: "Completed", color: "bg-green-500/20 text-green-400" },
  cancelled: { label: "Cancelled", color: "bg-red-500/20 text-red-400" }
};

const PRIORITY_CONFIG = {
  low: { label: "Low", color: "text-gray-400" },
  normal: { label: "Normal", color: "text-blue-400" },
  high: { label: "High", color: "text-orange-400" },
  urgent: { label: "Urgent", color: "text-red-400" }
};

const JobsTab = ({ clients = [], employees = [], quotes = [], onRefresh }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedJobs, setSelectedJobs] = useState([]);

  const [newJob, setNewJob] = useState({
    title: "", description: "", client_id: "", quote_id: "",
    scheduled_date: "", scheduled_time: "", estimated_duration: "",
    address: "", assigned_employees: [], priority: "normal", notes: ""
  });

  useEffect(() => { fetchJobs(); }, []);

  const fetchJobs = async () => {
    try {
      const res = await axios.get(`${API}/jobs`, { withCredentials: true });
      setJobs(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!newJob.title) { toast.error("Job title is required"); return; }
    try {
      await axios.post(`${API}/jobs`, newJob, { withCredentials: true });
      toast.success("Job created");
      setShowCreateModal(false);
      setNewJob({ title: "", description: "", client_id: "", quote_id: "", scheduled_date: "", scheduled_time: "", estimated_duration: "", address: "", assigned_employees: [], priority: "normal", notes: "" });
      fetchJobs();
      if (onRefresh) onRefresh();
    } catch (e) { toast.error("Failed to create job"); }
  };

  const handleStatusChange = async (jobId, newStatus) => {
    try {
      await axios.patch(`${API}/jobs/${jobId}`, { status: newStatus }, { withCredentials: true });
      toast.success(`Job marked as ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
      fetchJobs();
    } catch (e) { toast.error("Failed to update status"); }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm("Delete this job? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/jobs/${jobId}`, { withCredentials: true });
      toast.success("Job deleted");
      setSelectedJob(null);
      fetchJobs();
    } catch (e) { toast.error("Failed to delete job"); }
  };

  const handleAssign = async (jobId, employeeIds) => {
    try {
      await axios.patch(`${API}/jobs/${jobId}`, { assigned_employees: employeeIds }, { withCredentials: true });
      toast.success("Team updated");
      fetchJobs();
      if (selectedJob?.id === jobId) {
        const res = await axios.get(`${API}/jobs/${jobId}`, { withCredentials: true });
        setSelectedJob(res.data);
      }
    } catch (e) { toast.error("Failed to assign"); }
  };

  const handleBulkDelete = async () => {
    if (selectedJobs.length === 0) return;
    if (!window.confirm(`Delete ${selectedJobs.length} jobs? This cannot be undone.`)) return;
    try {
      await axios.post(`${API}/jobs/bulk-delete`, { ids: selectedJobs }, { withCredentials: true });
      toast.success(`${selectedJobs.length} jobs deleted`);
      setSelectedJobs([]);
      fetchJobs();
    } catch (e) { toast.error("Failed to delete jobs"); }
  };

  const toggleJobSelect = (jobId) => {
    setSelectedJobs(prev => prev.includes(jobId) ? prev.filter(id => id !== jobId) : [...prev, jobId]);
  };

  const filtered = jobs.filter(j => {
    if (statusFilter !== "all" && j.status !== statusFilter) return false;
    if (searchTerm && !j.title.toLowerCase().includes(searchTerm.toLowerCase()) && !j.client_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const openJobDetail = async (jobId) => {
    try {
      const res = await axios.get(`${API}/jobs/${jobId}`, { withCredentials: true });
      setSelectedJob(res.data);
    } catch (e) { toast.error("Failed to load job"); }
  };

  return (
    <div className="space-y-4" data-testid="jobs-tab">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex items-center space-x-3">
          <div className="relative flex-1 md:w-64">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0AAB2]" />
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search jobs..." data-testid="jobs-search"
              className="w-full pl-9 pr-4 py-2 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white text-sm placeholder:text-[#A0AAB2]/50 focus:outline-none focus:border-[#FF6B35]" />
          </div>
          <div className="flex gap-1">
            {["all", ...Object.keys(STATUS_CONFIG)].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)} data-testid={`job-filter-${s}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${statusFilter === s ? "bg-[#FF6B35] text-white" : "bg-[#1F2027] text-[#A0AAB2] border border-[#2A2B32]"}`}>
                {s === "all" ? "All" : STATUS_CONFIG[s]?.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {selectedJobs.length > 0 && (
            <button onClick={handleBulkDelete} data-testid="bulk-delete-jobs-btn"
              className="flex items-center space-x-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg text-sm font-medium hover:bg-red-500/30">
              <Trash2 size={16} /><span>Delete ({selectedJobs.length})</span>
            </button>
          )}
          <button onClick={() => setShowCreateModal(true)} data-testid="create-job-btn"
            className="flex items-center space-x-2 px-4 py-2 gradient-btn rounded-lg text-sm font-semibold">
            <Plus size={16} /><span>New Job</span>
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
          const count = jobs.filter(j => j.status === key).length;
          return (
            <div key={key} className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-3">
              <span className="text-[#A0AAB2] text-xs">{cfg.label}</span>
              <p className="text-xl font-bold text-white" data-testid={`job-count-${key}`}>{count}</p>
            </div>
          );
        })}
      </div>

      {/* Job List */}
      <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Jobs ({filtered.length})</h2>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#FF6B35]"></div>
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-[#A0AAB2] text-center py-8">No jobs found</p>
        ) : (
          <div className="space-y-3">
            {filtered.map(job => (
              <div key={job.id} onClick={() => openJobDetail(job.id)}
                className="bg-[#1F2027] border border-[#2A2B32] rounded-lg p-4 cursor-pointer hover:border-[#FF6B35]/50 transition-all"
                data-testid={`job-row-${job.id}`}>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="flex items-start space-x-3 flex-1">
                    <input type="checkbox" checked={selectedJobs.includes(job.id)}
                      onClick={e => { e.stopPropagation(); toggleJobSelect(job.id); }}
                      className="mt-1.5 rounded border-[#2A2B32]" />
                    <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="text-white font-semibold">{job.title}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_CONFIG[job.status]?.color || "bg-gray-500/20 text-gray-400"}`}>
                        {STATUS_CONFIG[job.status]?.label || job.status}
                      </span>
                      <span className={`text-xs ${PRIORITY_CONFIG[job.priority]?.color || "text-gray-400"}`}>
                        {PRIORITY_CONFIG[job.priority]?.label}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-[#A0AAB2] text-sm">
                      {job.client_name && <span>{job.client_name}</span>}
                      {job.scheduled_date && (
                        <span className="flex items-center space-x-1">
                          <Clock size={12} /><span>{job.scheduled_date}</span>
                        </span>
                      )}
                      {job.address && (
                        <span className="flex items-center space-x-1">
                          <MapPin size={12} /><span className="truncate max-w-[200px]">{job.address}</span>
                        </span>
                      )}
                    </div>
                    {job.assigned_employee_names?.length > 0 && (
                      <div className="flex items-center space-x-1 mt-1">
                        <Users size={12} className="text-[#FF6B35]" />
                        <span className="text-[#FF6B35] text-xs">{job.assigned_employee_names.join(", ")}</span>
                      </div>
                    )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="text-[#FF6B35] font-bold">${(job.total_price || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                      {job.amount_paid > 0 && <p className="text-green-400 text-xs">Paid: ${job.amount_paid.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>}
                      {job.total_price > 0 && job.amount_paid < job.total_price && (
                        <p className="text-yellow-400 text-xs">Owed: ${(job.total_price - (job.amount_paid || 0)).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                      )}
                    </div>
                    <ChevronRight size={16} className="text-[#A0AAB2]" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Job Detail Modal */}
      {selectedJob && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="job-detail-modal">
          <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl w-full max-w-2xl p-6 my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">{selectedJob.title}</h3>
              <button onClick={() => setSelectedJob(null)} className="p-2 text-[#A0AAB2] hover:text-white"><X size={20} /></button>
            </div>

            {/* Status + Actions */}
            <div className="flex flex-wrap gap-2 mb-4">
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <button key={key} onClick={() => { handleStatusChange(selectedJob.id, key); setSelectedJob(prev => ({...prev, status: key})); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${selectedJob.status === key ? cfg.color + " ring-1 ring-white/30" : "bg-[#1F2027] text-[#A0AAB2] border border-[#2A2B32]"}`}
                  data-testid={`set-status-${key}`}>
                  {cfg.label}
                </button>
              ))}
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-[#1F2027] rounded-lg p-3">
                <span className="text-[#A0AAB2] text-xs">Client</span>
                <p className="text-white font-medium">{selectedJob.client_name || "Unassigned"}</p>
              </div>
              <div className="bg-[#1F2027] rounded-lg p-3">
                <span className="text-[#A0AAB2] text-xs">Priority</span>
                <p className={`font-medium ${PRIORITY_CONFIG[selectedJob.priority]?.color}`}>{PRIORITY_CONFIG[selectedJob.priority]?.label}</p>
              </div>
              <div className="bg-[#1F2027] rounded-lg p-3">
                <span className="text-[#A0AAB2] text-xs">Scheduled Date</span>
                <p className="text-white font-medium">{selectedJob.scheduled_date || "Not set"}</p>
              </div>
              <div className="bg-[#1F2027] rounded-lg p-3">
                <span className="text-[#A0AAB2] text-xs">Address</span>
                <p className="text-white font-medium text-sm">{selectedJob.address || "Not set"}</p>
              </div>
            </div>

            {/* Financial */}
            <div className="bg-[#1F2027] rounded-lg p-4 mb-4">
              <h4 className="text-white font-semibold text-sm mb-3">Financial</h4>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <span className="text-[#A0AAB2] text-xs">Total Price</span>
                  <p className="text-[#FF6B35] font-bold">${(selectedJob.total_price || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                </div>
                <div>
                  <span className="text-[#A0AAB2] text-xs">Paid</span>
                  <p className="text-green-400 font-bold">${(selectedJob.amount_paid || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                </div>
                <div>
                  <span className="text-[#A0AAB2] text-xs">Outstanding</span>
                  <p className="text-yellow-400 font-bold">${((selectedJob.total_price || 0) - (selectedJob.amount_paid || 0)).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                </div>
              </div>
            </div>

            {/* Team Assignment */}
            <div className="bg-[#1F2027] rounded-lg p-4 mb-4">
              <h4 className="text-white font-semibold text-sm mb-3">Assigned Team</h4>
              <div className="flex flex-wrap gap-2">
                {employees.map(emp => {
                  const isAssigned = selectedJob.assigned_employees?.includes(emp.id);
                  return (
                    <button key={emp.id}
                      onClick={() => {
                        const curr = selectedJob.assigned_employees || [];
                        const updated = isAssigned ? curr.filter(id => id !== emp.id) : [...curr, emp.id];
                        handleAssign(selectedJob.id, updated);
                        setSelectedJob(prev => ({...prev, assigned_employees: updated}));
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${isAssigned ? "bg-[#FF6B35]/20 text-[#FF6B35] border border-[#FF6B35]/50" : "bg-[#2A2B32] text-[#A0AAB2]"}`}
                      data-testid={`assign-emp-${emp.id}`}>
                      {emp.name}
                    </button>
                  );
                })}
                {employees.length === 0 && <p className="text-[#A0AAB2] text-xs">No employees added yet. Go to Employees tab to add team members.</p>}
              </div>
            </div>

            {/* Tech Updates / Field Notes */}
            {selectedJob.tech_updates && selectedJob.tech_updates.length > 0 && (
              <div className="bg-[#1F2027] rounded-lg p-4 mb-4 border border-yellow-500/20" data-testid="tech-updates-section">
                <h4 className="text-white font-semibold text-sm mb-3 flex items-center space-x-2">
                  <FileText size={14} className="text-yellow-400" />
                  <span>Tech Updates ({selectedJob.tech_updates.length})</span>
                </h4>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {selectedJob.tech_updates.map((update, idx) => (
                    <div key={update.id || idx} className="bg-[#15161C] rounded-lg p-3 border border-[#2A2B32]" data-testid={`tech-update-${idx}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[#FF6B35] text-xs font-semibold">{update.employee_name}</span>
                        <span className="text-[#A0AAB2] text-[10px]">{update.timestamp ? new Date(update.timestamp).toLocaleString() : ""}</span>
                      </div>
                      {update.notes && (
                        <div className="mb-2">
                          <span className="text-[#A0AAB2] text-[10px] uppercase tracking-wide">Notes</span>
                          <p className="text-white text-sm mt-0.5">{update.notes}</p>
                        </div>
                      )}
                      {update.materials_needed && (
                        <div className="mb-2">
                          <span className="text-[#A0AAB2] text-[10px] uppercase tracking-wide flex items-center space-x-1">
                            <Wrench size={9} /><span>Materials Needed</span>
                          </span>
                          <p className="text-yellow-300 text-sm mt-0.5">{update.materials_needed}</p>
                        </div>
                      )}
                      {update.additional_work && (
                        <div className="mb-2">
                          <span className="text-[#A0AAB2] text-[10px] uppercase tracking-wide">Additional Work</span>
                          <p className="text-blue-300 text-sm mt-0.5">{update.additional_work}</p>
                        </div>
                      )}
                      {update.issues_found && (
                        <div>
                          <span className="text-[#A0AAB2] text-[10px] uppercase tracking-wide flex items-center space-x-1">
                            <AlertTriangle size={9} className="text-red-400" /><span>Issues Found</span>
                          </span>
                          <p className="text-red-300 text-sm mt-0.5">{update.issues_found}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {/* Update Quote Button */}
                {selectedJob.quote_id && (
                  <button
                    onClick={() => { window.location.href = `/admin/quotes/${selectedJob.quote_id}`; }}
                    className="mt-3 w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-yellow-500/20 text-yellow-400 rounded-lg text-sm font-medium hover:bg-yellow-500/30 transition-all border border-yellow-500/30"
                    data-testid="update-quote-btn">
                    <ExternalLink size={14} /><span>Update Quote Based on Tech Feedback</span>
                  </button>
                )}
              </div>
            )}

            {/* Acceptance Info */}
            {selectedJob.accepted_by_name && (
              <div className="bg-[#1F2027] rounded-lg p-3 mb-4 flex items-center space-x-2" data-testid="job-acceptance-info">
                <CheckCircle size={14} className="text-green-400" />
                <span className="text-[#A0AAB2] text-sm">
                  Accepted by <span className="text-white font-medium">{selectedJob.accepted_by_name}</span>
                  {selectedJob.accepted_at && <span className="text-[#A0AAB2]"> on {new Date(selectedJob.accepted_at).toLocaleString()}</span>}
                </span>
              </div>
            )}

            {/* Notes */}
            {selectedJob.description && (
              <div className="bg-[#1F2027] rounded-lg p-4 mb-4">
                <h4 className="text-white font-semibold text-sm mb-2">Description</h4>
                <p className="text-[#A0AAB2] text-sm whitespace-pre-wrap">{selectedJob.description}</p>
              </div>
            )}

            {/* Delete */}
            <button onClick={() => handleDelete(selectedJob.id)} data-testid="delete-job-btn"
              className="flex items-center space-x-2 px-4 py-2 bg-red-500/10 text-red-400 rounded-lg text-sm hover:bg-red-500/20 transition-all">
              <Trash2 size={16} /><span>Delete Job</span>
            </button>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="create-job-modal">
          <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl w-full max-w-lg p-6 my-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Create New Job</h3>
              <button onClick={() => setShowCreateModal(false)} className="p-2 text-[#A0AAB2] hover:text-white"><X size={20} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-[#A0AAB2] text-sm mb-1 block">Title *</label>
                <input type="text" value={newJob.title} onChange={e => setNewJob(p => ({...p, title: e.target.value}))}
                  className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white" placeholder="Job title" data-testid="new-job-title" />
              </div>
              <div>
                <label className="text-[#A0AAB2] text-sm mb-1 block">Client</label>
                <select value={newJob.client_id} onChange={e => setNewJob(p => ({...p, client_id: e.target.value}))}
                  className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white" data-testid="new-job-client">
                  <option value="">Select client (optional)</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[#A0AAB2] text-sm mb-1 block">From Quote</label>
                <select value={newJob.quote_id} onChange={e => setNewJob(p => ({...p, quote_id: e.target.value}))}
                  className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white" data-testid="new-job-quote">
                  <option value="">Select quote (optional)</option>
                  {quotes.filter(q => ["approved", "signed", "invoiced"].includes(q.status)).map(q => (
                    <option key={q.id} value={q.id}>{q.quote_number} - {q.title} (${q.total_price})</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[#A0AAB2] text-sm mb-1 block">Date</label>
                  <input type="date" value={newJob.scheduled_date} onChange={e => setNewJob(p => ({...p, scheduled_date: e.target.value}))}
                    className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white" data-testid="new-job-date" />
                </div>
                <div>
                  <label className="text-[#A0AAB2] text-sm mb-1 block">Time</label>
                  <input type="time" value={newJob.scheduled_time} onChange={e => setNewJob(p => ({...p, scheduled_time: e.target.value}))}
                    className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white" data-testid="new-job-time" />
                </div>
              </div>
              <div>
                <label className="text-[#A0AAB2] text-sm mb-1 block">Address</label>
                <input type="text" value={newJob.address} onChange={e => setNewJob(p => ({...p, address: e.target.value}))}
                  className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white" placeholder="Job location" data-testid="new-job-address" />
              </div>
              <div>
                <label className="text-[#A0AAB2] text-sm mb-1 block">Priority</label>
                <select value={newJob.priority} onChange={e => setNewJob(p => ({...p, priority: e.target.value}))}
                  className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white" data-testid="new-job-priority">
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div>
                <label className="text-[#A0AAB2] text-sm mb-1 block">Assign Team</label>
                <div className="flex flex-wrap gap-2">
                  {employees.map(emp => {
                    const sel = newJob.assigned_employees.includes(emp.id);
                    return (
                      <button key={emp.id} type="button"
                        onClick={() => setNewJob(p => ({...p, assigned_employees: sel ? p.assigned_employees.filter(id => id !== emp.id) : [...p.assigned_employees, emp.id]}))}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium ${sel ? "bg-[#FF6B35]/20 text-[#FF6B35] border border-[#FF6B35]/50" : "bg-[#2A2B32] text-[#A0AAB2]"}`}>
                        {emp.name}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="text-[#A0AAB2] text-sm mb-1 block">Notes</label>
                <textarea value={newJob.notes} onChange={e => setNewJob(p => ({...p, notes: e.target.value}))} rows={2}
                  className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white resize-none" data-testid="new-job-notes" />
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button onClick={() => setShowCreateModal(false)} className="flex-1 py-3 bg-[#2A2B32] text-white rounded-lg">Cancel</button>
              <button onClick={handleCreate} className="flex-1 py-3 gradient-btn rounded-lg font-semibold" data-testid="submit-create-job">Create Job</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobsTab;
